import { useEffect, useMemo, useState } from "react";
import type {
  AgentDefinition,
  AgentWorkspaceDefinition,
  Artifact,
  AvailableToolDefinition,
  GitRepositoryDefinition,
  McpServerDefinition,
  McpToolDefinition,
  Model,
  SkillDefinition,
  ThinkingLevel,
} from "@lumen/shared";
import { api } from "../../api";
import { t } from "../../i18n";
import { Toggle } from "../Toggle";
import { ActivitySettingsPanel } from "../composer/ActivitySettingsPanel";
import { ComposerToolPopupTools } from "../composer/ComposerToolPopupTools";
import type {
  ComposerActivity,
  ComposerActivitySettings,
  ComposerOptions,
} from "../composer/Composer";
import { activityLabel } from "../composer/ComposerActivityUtils";
import { supportsActivity } from "../../pages/chat/ChatPage.utils";

const isContainerWorkspaceType = (type: string) =>
  ["container", "process-sandbox", "rootless-podman"].includes(type);

type NonAskActivity = Exclude<ComposerActivity, "ask">;

type ComposerDefaultsPanelProps = {
  value: Partial<ComposerOptions>;
  models: Model[];
  availableActivities: ComposerActivity[];
  organizationDefaultsOnly?: boolean;
  onChange: (value: Partial<ComposerOptions>) => void;
};

type McpToolState = {
  status: "loading" | "ready" | "error";
  tools: McpToolDefinition[];
};

const emptyMcpTools: Record<string, McpToolState> = {};

function updateGeneration(
  value: Partial<ComposerOptions>,
  patch: Partial<NonNullable<ComposerOptions["generationParameters"]>>,
) {
  return {
    ...value,
    generationParameters: {
      ...(value.generationParameters ?? {}),
      ...patch,
    },
  };
}

export function ComposerDefaultsPanel({
  value,
  models,
  availableActivities,
  organizationDefaultsOnly = false,
  onChange,
}: ComposerDefaultsPanelProps) {
  const [tools, setTools] = useState<AvailableToolDefinition[]>([]);
  const [skills, setSkills] = useState<SkillDefinition[]>([]);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [mcpServers, setMcpServers] = useState<McpServerDefinition[]>([]);
  const [mcpTools, setMcpTools] =
    useState<Record<string, McpToolState>>(emptyMcpTools);
  const [workspaces, setWorkspaces] = useState<AgentWorkspaceDefinition[]>([]);
  const [repositories, setRepositories] = useState<GitRepositoryDefinition[]>(
    [],
  );
  const [agents, setAgents] = useState<AgentDefinition[]>([]);
  const [resourcesLoaded, setResourcesLoaded] = useState(false);
  const [activityDetail, setActivityDetail] = useState<NonAskActivity | null>(
    null,
  );

  useEffect(() => {
    let active = true;
    const load = <T,>(path: string) => api<T>(path).catch(() => [] as T);
    void Promise.all([
      load<AvailableToolDefinition[]>("/tools/available"),
      load<SkillDefinition[]>("/skills"),
      load<Artifact[]>("/artifacts"),
      load<McpServerDefinition[]>("/mcp-servers/available"),
      load<AgentWorkspaceDefinition[]>("/agent-workspaces/available"),
      load<GitRepositoryDefinition[]>("/git-repositories"),
      load<AgentDefinition[]>("/agents/available"),
    ]).then(
      ([
        loadedTools,
        loadedSkills,
        loadedArtifacts,
        loadedMcpServers,
        loadedWorkspaces,
        loadedRepositories,
        loadedAgents,
      ]) => {
        if (!active) return;
        setTools(loadedTools);
        setSkills(loadedSkills);
        setArtifacts(loadedArtifacts);
        setMcpServers(loadedMcpServers);
        setWorkspaces(loadedWorkspaces);
        setRepositories(loadedRepositories.filter((row) => row.enabled));
        setAgents(loadedAgents);
        setResourcesLoaded(true);
      },
    );
    return () => {
      active = false;
    };
  }, []);

  const activityModels = useMemo(
    () =>
      Object.fromEntries(
        availableActivities
          .filter((activity): activity is NonAskActivity => activity !== "ask")
          .map((activity) => [
            activity,
            models.filter((model) => supportsActivity(model, activity)),
          ]),
      ) as Record<NonAskActivity, Model[]>,
    [availableActivities, models],
  );
  const generation = value.generationParameters ?? {};
  const enabledActivities = [
    "ask" as const,
    ...(generation.enabledActivities ?? []).filter(
      (activity): activity is NonAskActivity =>
        availableActivities.includes(activity),
    ),
  ];
  const activitySettings = (generation.activitySettings ??
    {}) as ComposerActivitySettings;
  const mcpServerIds = value.mcpServerIds ?? [];
  const mcpToolSelections = value.mcpToolSelections ?? {};
  const workspaceIds = value.agentWorkspaceIds ?? [];
  const repositoryIds = value.repositoryIds ?? {};
  const visibleTools = organizationDefaultsOnly
    ? tools.filter(
        (item) =>
          (item as AvailableToolDefinition & { scope?: string }).scope !==
          "personal",
      )
    : tools;
  const visibleSkills = organizationDefaultsOnly
    ? skills.filter((item) => item.scope !== "personal")
    : skills;
  const visibleMcpServers = organizationDefaultsOnly
    ? mcpServers.filter((item) => item.scope !== "personal")
    : mcpServers;
  const visibleWorkspaces = organizationDefaultsOnly
    ? workspaces.filter((item) => item.scope !== "personal")
    : workspaces;
  const visibleAgents = organizationDefaultsOnly
    ? agents.filter((item) => item.scope !== "personal")
    : agents;

  useEffect(() => {
    if (!organizationDefaultsOnly || !resourcesLoaded) return;
    const allowedToolIds = new Set(visibleTools.map((item) => item.id));
    const allowedSkillIds = new Set(visibleSkills.map((item) => item.id));
    const allowedMcpServerIds = new Set(
      visibleMcpServers.map((item) => item.id),
    );
    const allowedWorkspaceIds = new Set(
      visibleWorkspaces.map((item) => item.id),
    );
    const allowedAgentIds = new Set(visibleAgents.map((item) => item.id));
    const nextValue: Partial<ComposerOptions> = {
      ...value,
      toolIds: (value.toolIds ?? []).filter((id) => allowedToolIds.has(id)),
      skillIds: (value.skillIds ?? []).filter((id) => allowedSkillIds.has(id)),
      mcpServerIds: (value.mcpServerIds ?? []).filter((id) =>
        allowedMcpServerIds.has(id),
      ),
      mcpToolSelections: Object.fromEntries(
        Object.entries(value.mcpToolSelections ?? {}).filter(([id]) =>
          allowedMcpServerIds.has(id),
        ),
      ),
      agentWorkspaceIds: (value.agentWorkspaceIds ?? []).filter((id) =>
        allowedWorkspaceIds.has(id),
      ),
      agentWorkspaceId:
        value.agentWorkspaceId &&
        allowedWorkspaceIds.has(value.agentWorkspaceId)
          ? value.agentWorkspaceId
          : null,
      agentId:
        value.agentId && allowedAgentIds.has(value.agentId)
          ? value.agentId
          : null,
      artifactIds: [],
      repositoryId: null,
      repositoryIds: {},
    };
    if (JSON.stringify(nextValue) !== JSON.stringify(value))
      onChange(nextValue);
  }, [
    organizationDefaultsOnly,
    resourcesLoaded,
    value,
    visibleAgents,
    visibleMcpServers,
    visibleSkills,
    visibleTools,
    visibleWorkspaces,
    onChange,
  ]);

  const setValue = (patch: Partial<ComposerOptions>) =>
    onChange({ ...value, ...patch });
  const setGeneration = (
    patch: Partial<NonNullable<ComposerOptions["generationParameters"]>>,
  ) => onChange(updateGeneration(value, patch));

  const loadMcpTools = async (serverId: string) => {
    if (mcpTools[serverId]?.status === "loading") return;
    setMcpTools((current) => ({
      ...current,
      [serverId]: { status: "loading", tools: [] },
    }));
    try {
      const rows = await api<McpToolDefinition[]>(
        `/mcp-servers/${serverId}/tools`,
      );
      setMcpTools((current) => ({
        ...current,
        [serverId]: { status: "ready", tools: rows },
      }));
      if (mcpToolSelections[serverId] === undefined)
        setValue({
          mcpToolSelections: {
            ...mcpToolSelections,
            [serverId]: rows.map((tool) => tool.name),
          },
        });
    } catch {
      setMcpTools((current) => ({
        ...current,
        [serverId]: { status: "error", tools: [] },
      }));
    }
  };

  const toggleWorkspace = (workspaceId: string) => {
    const next = workspaceIds.includes(workspaceId)
      ? workspaceIds.filter((id) => id !== workspaceId)
      : [...workspaceIds, workspaceId];
    setValue({
      agentWorkspaceIds: next,
      agentWorkspaceId: next.length === 1 ? next[0] : null,
      ...(next.length !== 1 ? { repositoryId: null } : {}),
    });
  };
  const toggleRepository = (workspaceId: string, repositoryId: string) => {
    const selected = repositoryIds[workspaceId] ?? [];
    const next = selected.includes(repositoryId)
      ? selected.filter((id) => id !== repositoryId)
      : [...selected, repositoryId];
    const nextRepositories = { ...repositoryIds, [workspaceId]: next };
    setValue({
      repositoryIds: nextRepositories,
      repositoryId:
        workspaceIds.length === 1 && next.length === 1 ? next[0] : null,
    });
  };

  return (
    <div className="space-y-5">
      <section>
        <h3 className="font-semibold">{t("Tools and activities")}</h3>
        <p className="mt-1 text-sm text-stone-500">
          {t(
            "These selections become the starting configuration for each new conversation.",
          )}
        </p>
        <div className="mt-3 rounded-xl border border-stone-200 p-2 dark:border-white/10">
          <ComposerToolPopupTools
            section="tools"
            activityModels={activityModels}
            activitySettings={activitySettings}
            enabledActivities={enabledActivities}
            mcpServers={[]}
            mcpServerIds={[]}
            mcpToolSelections={{}}
            mcpTools={emptyMcpTools}
            onActivitySettingsChange={(
              activity: NonAskActivity,
              settings: unknown,
            ) =>
              setGeneration({
                activitySettings: { ...activitySettings, [activity]: settings },
              })
            }
            onOpenActivity={setActivityDetail}
            onOpenMcp={() => undefined}
            onOpenSettings={() => undefined}
            onSetupMcp={() => undefined}
            onToggle={(toolId: string) =>
              setValue({
                toolIds: (value.toolIds ?? []).includes(toolId)
                  ? (value.toolIds ?? []).filter((id) => id !== toolId)
                  : [...(value.toolIds ?? []), toolId],
              })
            }
            onToggleActivity={(activity: NonAskActivity) =>
              setGeneration({
                enabledActivities: enabledActivities.includes(activity)
                  ? enabledActivities.filter(
                      (item): item is NonAskActivity =>
                        item !== activity && item !== "ask",
                    )
                  : [
                      ...enabledActivities.filter(
                        (item): item is NonAskActivity => item !== "ask",
                      ),
                      activity,
                    ],
              })
            }
            onToggleMcp={() => undefined}
            toolIds={value.toolIds ?? []}
            tools={visibleTools}
          />
        </div>
        {activityDetail && (
          <div className="mt-3 rounded-xl border border-stone-200 p-3 dark:border-white/10">
            <div className="mb-2 flex items-center justify-between">
              <h4 className="font-semibold">{activityLabel(activityDetail)}</h4>
              <button
                type="button"
                className="button px-2 py-1 text-xs"
                onClick={() => setActivityDetail(null)}
              >
                {t("Done")}
              </button>
            </div>
            <ActivitySettingsPanel
              activity={activityDetail}
              models={activityModels[activityDetail] ?? []}
              settings={activitySettings[activityDetail] ?? {}}
              allowModelSelection={false}
              onChange={(settings) =>
                setGeneration({
                  activitySettings: {
                    ...activitySettings,
                    [activityDetail]: settings,
                  },
                })
              }
            />
          </div>
        )}
      </section>

      <section>
        <h3 className="font-semibold">{t("Ask mode parameters")}</h3>
        <div className="mt-2 grid gap-3 sm:grid-cols-3">
          {(
            [
              ["temperature", t("Temperature"), 0, 2, 0.1],
              ["topP", t("Top P"), 0, 1, 0.05],
              ["maxTokens", t("Max tokens"), 1, 1000000, 1],
              ["frequencyPenalty", t("Frequency penalty"), -2, 2, 0.1],
              ["presencePenalty", t("Presence penalty"), -2, 2, 0.1],
              ["seed", t("Seed"), -2147483648, 2147483647, 1],
            ] as const
          ).map(([key, label, min, max, step]) => (
            <label key={key} className="block">
              <span className="label">{label}</span>
              <input
                type="number"
                className="field"
                min={min}
                max={max}
                step={step}
                value={
                  typeof generation[key] === "number"
                    ? String(generation[key])
                    : ""
                }
                placeholder={t("Default")}
                onChange={(event) =>
                  setGeneration({
                    [key]: event.target.value
                      ? Number(event.target.value)
                      : undefined,
                  })
                }
              />
            </label>
          ))}
        </div>
        <label className="mt-3 flex items-start gap-3 rounded-xl border border-stone-200 p-3 dark:border-white/10">
          <Toggle
            className="mt-1"
            checked={generation.goal === true}
            onChange={(event) =>
              setGeneration({ goal: event.target.checked ? true : undefined })
            }
          />
          <span>
            <strong className="block text-sm">{t("Goal mode")}</strong>
            <span className="text-xs text-stone-400">
              {t("Enable goal mode for new Ask conversations in this project.")}
            </span>
          </span>
        </label>
      </section>

      {visibleMcpServers.length > 0 && (
        <section>
          <h3 className="font-semibold">{t("MCP servers")}</h3>
          <div className="mt-2 space-y-2">
            {visibleMcpServers.map((server) => {
              const selected = mcpServerIds.includes(server.id);
              const selectedTools = mcpToolSelections[server.id] ?? [];
              const state = mcpTools[server.id];
              return (
                <div
                  key={server.id}
                  className="rounded-xl border border-stone-200 p-3 dark:border-white/10"
                >
                  <label className="flex items-start gap-3">
                    <Toggle
                      className="mt-1"
                      checked={selected}
                      disabled={
                        server.authType !== "none" &&
                        !server.userConfigConfigured
                      }
                      onChange={() => {
                        const next = selected
                          ? mcpServerIds.filter((id) => id !== server.id)
                          : [...mcpServerIds, server.id];
                        setValue({ mcpServerIds: next });
                        if (!selected) void loadMcpTools(server.id);
                      }}
                    />
                    <span className="min-w-0">
                      <strong className="block">{server.name}</strong>
                      <span className="text-xs text-stone-400">
                        {server.description || server.endpointUrl}
                      </span>
                    </span>
                  </label>
                  {selected && state?.status === "ready" && (
                    <div className="mt-3 space-y-1 pl-7">
                      {state.tools.map((tool) => (
                        <label
                          key={tool.name}
                          className="flex items-center gap-2 text-sm"
                        >
                          <Toggle
                            checked={selectedTools.includes(tool.name)}
                            onChange={() =>
                              setValue({
                                mcpToolSelections: {
                                  ...mcpToolSelections,
                                  [server.id]: selectedTools.includes(tool.name)
                                    ? selectedTools.filter(
                                        (name) => name !== tool.name,
                                      )
                                    : [...selectedTools, tool.name],
                                },
                              })
                            }
                          />
                          <span>{tool.title || tool.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {selected && !state && (
                    <button
                      type="button"
                      className="button mt-2 ml-7 px-2 py-1 text-xs"
                      onClick={() => void loadMcpTools(server.id)}
                    >
                      {t("Configure functions")}
                    </button>
                  )}
                  {selected && state?.status === "loading" && (
                    <p className="mt-2 pl-7 text-xs text-stone-400">
                      {t("Loading functions…")}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section>
        <h3 className="font-semibold">{t("Environment and agent")}</h3>
        <label className="mt-2 block max-w-xl">
          <span className="label">{t("Agent")}</span>
          <select
            className="field"
            value={value.agentId ?? ""}
            onChange={(event) =>
              setValue({ agentId: event.target.value || null })
            }
          >
            <option value="">{t("Default")}</option>
            {visibleAgents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
              </option>
            ))}
          </select>
        </label>
        {visibleWorkspaces.length > 0 && (
          <div className="mt-3 space-y-2">
            {visibleWorkspaces.map((workspace) => (
              <div
                key={workspace.id}
                className="rounded-xl border border-stone-200 p-3 dark:border-white/10"
              >
                <label className="flex items-center gap-3 text-sm">
                  <Toggle
                    checked={workspaceIds.includes(workspace.id)}
                    onChange={() => toggleWorkspace(workspace.id)}
                  />
                  <span>
                    <strong className="block">{workspace.name}</strong>
                    <span className="text-xs text-stone-400">
                      {workspace.description || workspace.slug}
                    </span>
                  </span>
                </label>
                {workspaceIds.includes(workspace.id) &&
                  isContainerWorkspaceType(workspace.type) &&
                  !organizationDefaultsOnly &&
                  repositories.length > 0 && (
                    <div className="mt-2 space-y-1 pl-7">
                      {repositories.map((repository) => (
                        <label
                          key={repository.id}
                          className="flex items-center gap-2 text-sm"
                        >
                          <Toggle
                            checked={(
                              repositoryIds[workspace.id] ?? []
                            ).includes(repository.id)}
                            onChange={() =>
                              toggleRepository(workspace.id, repository.id)
                            }
                          />
                          <span>{repository.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}
      </section>

      {(visibleSkills.length > 0 ||
        (!organizationDefaultsOnly && artifacts.length > 0)) && (
        <section>
          <h3 className="font-semibold">{t("Skills and artifacts")}</h3>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            {visibleSkills.length > 0 && (
              <div className="rounded-xl border border-stone-200 p-3 dark:border-white/10">
                <h4 className="mb-2 text-sm font-semibold">{t("Skills")}</h4>
                <div className="space-y-1">
                  {visibleSkills.map((skill) => (
                    <label
                      key={skill.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Toggle
                        checked={(value.skillIds ?? []).includes(skill.id)}
                        onChange={() =>
                          setValue({
                            skillIds: (value.skillIds ?? []).includes(skill.id)
                              ? (value.skillIds ?? []).filter(
                                  (id) => id !== skill.id,
                                )
                              : [...(value.skillIds ?? []), skill.id],
                          })
                        }
                      />
                      <span className="truncate">{skill.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {!organizationDefaultsOnly && artifacts.length > 0 && (
              <div className="rounded-xl border border-stone-200 p-3 dark:border-white/10">
                <h4 className="mb-2 text-sm font-semibold">{t("Artifacts")}</h4>
                <div className="space-y-1">
                  {artifacts.map((artifact) => (
                    <label
                      key={artifact.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Toggle
                        checked={(value.artifactIds ?? []).includes(
                          artifact.id,
                        )}
                        onChange={() =>
                          setValue({
                            artifactIds: (value.artifactIds ?? []).includes(
                              artifact.id,
                            )
                              ? (value.artifactIds ?? []).filter(
                                  (id) => id !== artifact.id,
                                )
                              : [...(value.artifactIds ?? []), artifact.id],
                          })
                        }
                      />
                      <span className="truncate">{artifact.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <label className="block max-w-xs">
        <span className="label">{t("Thinking level")}</span>
        <select
          className="field"
          value={value.thinkingLevel ?? ""}
          onChange={(event) =>
            setValue({
              thinkingLevel: (event.target.value || undefined) as
                ThinkingLevel | undefined,
            })
          }
        >
          <option value="">{t("Default")}</option>
          <option value="low">{t("Low")}</option>
          <option value="medium">{t("Medium")}</option>
          <option value="high">{t("High")}</option>
        </select>
      </label>
    </div>
  );
}
